<template>
  <list listTitle="Pedidos" filterTitle="Listagem de pedidos" :loading="ordersFetching" :total="ordersTotal" :sortDefault="{ field: 'createdAt', order: 'desc' }" :sortFields="['id', 'createdAt', 'clientName']" @change="onChange">
    <sync-dialog v-model="showSyncDialog"/>

    <button @click="showSyncDialog = true" slot="header-actions" class="hint hint--left btn btn--info" data-label="Sincronizar manualmente com o marketplace">
      <i class="icon-sync icon-download2"/>
    </button>

    <div slot="top-actions">
      <div class="l-row">
        <div class="l-col-12@md inline-flex">
          <button data-label="Selecionar todos" @click="selectAll()" class="hint hint--right btn btn--lg btn--default">
            <i class="check-box" v-bind:class="selectClass"></i>
          </button>
          <bulk-actions :actions="bulkActions" icon="icon-cloud-upload" @apply="applyBulkAction"/>
        </div>
      </div>
    </div>

    <template slot="filters">
      <form-group label="Identificador">
        <input class="form-input" v-model="options.identifier">
        <small class="form-help">Identificador do marketplace, expedidor, item ou rastreamento.</small>
      </form-group>

      <form-group label="Marketplace">
        <select v-model="options.marketplaceIdentifier" class="form-select">
          <option :value=null>Todos</option>
          <option v-for="marketplace in marketplaces" :value="marketplace" :key="marketplace">{{ $t(`${marketplace}.label`) }}</option>
        </select>
      </form-group>

      <form-group v-if="options.marketplaceIdentifier === 'mercadoLivre'" label="Conta do Mercado Livre">
        <select v-model="options.mercadoLivreAccount" class="form-select">
          <option :value=null>Todas</option>
          <option v-for="mercadoLivreAccount in mercadoLivreAccounts" :value="mercadoLivreAccount.identifier" :key="mercadoLivreAccount.identifier">{{ mercadoLivreAccount.nickname }}</option>
        </select>
      </form-group>

      <form-group v-if="options.marketplaceIdentifier === 'shopee'" label="Conta do Shopee">
        <select v-model="options.shopeeAccount" class="form-select">
          <option :value=null>Todas</option>
          <option v-for="shopeeAccount in shopeeAccounts" :value="shopeeAccount.shopId" :key="shopeeAccount.shopId">{{ shopeeAccount.shopName }}</option>
        </select>
      </form-group>

      <form-group label="Nome do cliente">
        <input class="form-input" v-model="options.customerName">
      </form-group>

      <form-group label="CPF do cliente">
        <input class="form-input" v-model="options.customerDocumentNumber">
      </form-group>

      <form-group label="Data do Pedido (Marketplace)">
        <el-date-picker
          v-model="options.marketplaceDate"
          type="daterange"
          format="dd/MM/yyyy"
          start-placeholder="Data Inicial"
          end-placeholder="Data Final">
        </el-date-picker>
      </form-group>

      <form-group label="Status do pedido">
        <select v-model="options.status" class="form-select">
          <option :value=null>Todos</option>
          <option v-for="status in ordersStatuses" :key="status" :value="status">{{ $t(status).label }}</option>
        </select>
      </form-group>

      <form-group label="Status do pedido no expedidor">
        <select v-model="options.expediterStatus" class="form-select">
          <option :value=null>Todos</option>
          <option v-for="status in orderExpediterStatuses" :key="status" :value="status">{{ $t(status).label }}</option>
        </select>
      </form-group>

      <form-group label="Pedido Faturado">
        <select v-model="options.invoiced" class="form-select">
          <option :value=null>Todos</option>
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </select>
      </form-group>

      <form-group label="Pedido Enviado">
        <select v-model="options.shipped" class="form-select">
          <option :value=null>Todos</option>
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </select>
      </form-group>

      <form-group label="Pedido Entregue">
        <select v-model="options.delivered" class="form-select">
          <option :value=null>Todos</option>
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </select>
      </form-group>
    </template>

    <div class="l-col-12@md" v-for="(item, index) in ordersItems" :key="index">
      <order-card ref="items" :order="item"/>
    </div>
    <div class="l-col u-textCenter" v-if="!ordersItems.length">
      <panel>
        Nenhum pedido encontrado.
      </panel>
    </div>
  </list>
</template>

<script>
  import { merge } from 'lodash'
  import { mapActions } from 'vuex'
  import { mapFields } from 'vuex-map-fields'
  import OrderCard from '@/components/Order/Shared/OrderCard'
  import SyncDialog from '@/components/Order/Shared/SyncDialog'
  import FormGroup from '@/components/template/FormGroup'
  import List from '@/components/shared/list/Paginator'
  import DateInput from '@/components/template/DateInput'
  import Panel from '@/components/template/Panel'
  import BulkActions from '@/components/shared/BulkActions'
  import notifier from '@/notifier'
  import api from '@/api'
  import VueTagsInput from '@johmun/vue-tags-input'

  export default {
    data: () => ({
      showSyncDialog: false,
      options: {
        identifier: '',
        expediterStatus: null,
        marketplaceDate: [],
        marketplaceIdentifier: null,
        mercadoLivreAccount: null,
        shopeeAccount: null,
        customerName: null,
        customerDocumentNumber: '',
        status: null,
        invoiced: null,
        shipped: null,
        delivered: null
      },
      marketplaces: [],
      mercadoLivreAccounts: [],
      shopeeAccounts: [],
      bulkActions: [
        {
          label: 'printShipmentLabelMercadoLivre',
          module: {
            type: 'marketplace',
            identifier: 'mercadoLivre'
          },
          value: {
            type: 'backend'
          }
        },
        {
          label: 'printShipmentLabelIntegracommerce',
          module: {
            type: 'marketplace',
            identifier: 'integracommerce'
          },
          value: {
            type: 'backend'
          }
        }
      ],
      selectedAll: false
    }),
    created () {
      const actualDate = new Date()
      const oldDate = new Date()
      oldDate.setDate(actualDate.getDate() - 7)
      this.options.marketplaceDate.push(oldDate, actualDate)
      merge(this.options, this.ordersFilter)
    },
    async mounted () {
      try {
        this.marketplaces = await api.getOrdersMarketplaces()
      } catch (err) {
        this.marketplaces = []
      }

      try {
        this.mercadoLivreAccounts = await api.getOrdersMercadoLivreAccounts()
      } catch (err) {
        this.mercadoLivreAccounts = []
      }

      try {
        this.shopeeAccounts = await api.getOrdersShopeeAccounts()
      } catch (err) {
        this.shopeeAccounts = []
      }
    },
    methods: {
      ...mapActions('orders', [
        'fetch',
        'fillFilter'
      ]),
      ...mapActions('itemsSelected', [
        'clear'
      ]),
      async onChange (options) {
        merge(this.options, options)

        const codes = []

        if (this.options.shippingItemsCode !== '') {
          codes.push(this.options.shippingItemsCode)
        }

        for (let i in this.shippingItemsCodes) {
          codes.push(this.shippingItemsCodes[i].text)
        }

        this.options.shippingItemsCode = codes.join(',')

        await this.fetch(this.options)

        this.fillFilter(this.options)

        this.options.shippingItemsCode = ''
      },
      selectAll () {
        this.selectedAll = !this.selectedAll

        for (let i in this.$refs.items) {
          this.$refs.items[i].onSelect(this.selectedAll)
        }
      },
      clearSelected () {
        this.clear()

        for (let i in this.$refs.items) {
          this.$refs.items[i].onSelect(false)
        }
      },
      applyBulkAction (action, module) {
        if (action.type === 'front') {
          this.$router.push(`/${action}`)
        }
        if (action.type === 'backend') {
          api.createPrintShipmentLabelTask(module.type, module.identifier, this.itemsSelectedItemsSelected.map(item => item._id.$oid))
            .then(task => {
              notifier.success('Tarefa criada com sucesso', 'Sucesso')

              this.$router.push(`/tool/batch/tasks/detail/${task._id.$oid}`)
              this.clearSelected()
            })
            .catch(error => {
              let message = 'Erro ao criar a tarefa'
              if (error.response) {
                if (error.response.data.message) {
                  message = error.response.data.message
                }
              }
              notifier.error(message, 'Falha')

              this.clearSelected()
            })
        }
      }
    },
    computed: {
      ...mapFields({
        ordersStatuses: 'orders.statuses',
        ordersItems: 'orders.items',
        ordersTotal: 'orders.total',
        ordersFetching: 'orders.fetching',
        ordersFilter: 'orders.filter',
        orderExpediterStatuses: 'orders.expediterStatuses',
        itemsSelectedTotalSelected: 'itemsSelected.totalSelected',
        itemsSelectedItemsSelected: 'itemsSelected.itemsSelected'
      }),
      selectClass () {
        return {
          'checked': this.selectedAll
        }
      }
    },
    watch: {
      'showSyncDialog' (value) {
        if (!value) {
          this.fetch(this.options)
        }
      },
      itemsSelectedTotalSelected () {
        this.selectedAll = false
        if (this.ordersItems.length) {
          if (this.itemsSelectedTotalSelected === this.ordersItems.length) {
            this.selectedAll = true
          }
        }
      }
    },
    components: {
      List,
      OrderCard,
      FormGroup,
      SyncDialog,
      DateInput,
      Panel,
      BulkActions,
      VueTagsInput
    }
  }
</script>

<style scoped lang="sass">
  .logo
    max-height: 30px
    margin-right: 20px

  .title
    margin-top: 16px
    margin-left: 20px

  .paginator
    float: right

  .count-selected
    margin-top: 3px

  .selectAll
    width: 48px
    padding: 16px

  .btn--default
    margin-right: 5px

  .inline-flex
    display: inline-flex

  .check-box
    border-color: #7872ff
</style>



// WEBPACK FOOTER //
// Base.vue?76d55b8c